import React from 'react';
import Icon from '../../../components/AppIcon';

const KeyMetricsDisplay = () => {
  const keyMetrics = {
    walletHealth: {
      score: 95,
      status: 'excellent',
      lastCheck: '2 minutes ago',
      issues: 0,
      details: {
        encryption: 'AES-256 Active',
        backupStatus: 'Up to date',
        keyRotation: 'Scheduled',
        accessControl: 'Secure'
      }
    },
    blockchainSync: {
      score: 100,
      status: 'synced',
      lastSync: 'Just now',
      blockHeight: 18542891,
      details: {
        network: 'Ethereum Mainnet',
        gasPrice: '12 gwei',
        confirmations: 15,
        latency: '0.8s'
      }
    },
    privacySettings: {
      score: 88,
      status: 'good',
      activeControls: 12,
      dataSharing: 'minimal',
      details: {
        zeroKnowledgeProofs: 'Enabled',
        selectiveDisclosure: 'Active',
        dataMinimization: 'Enforced',
        consentManagement: 'Configured'
      }
    },
    credentialActivity: {
      totalCredentials: 24,
      activeCredentials: 22,
      recentVerifications: 8,
      pendingRequests: 3,
      details: {
        issuedThisMonth: 4,
        verifiedThisWeek: 12,
        sharedCredentials: 6,
        revokedCredentials: 0
      }
    }
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getHealthBgColor = (score) => {
    if (score >= 90) return 'bg-success';
    if (score >= 70) return 'bg-warning';
    return 'bg-error';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return 'CheckCircle2';
      case 'synced': return 'Wifi';
      case 'good': return 'Shield';
      default: return 'AlertCircle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">System Metrics</h2>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-sm text-muted-foreground">Live monitoring</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Wallet Health */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getHealthBgColor(keyMetrics?.walletHealth?.score)}/10`}>
                <Icon name="Wallet" size={20} className={getHealthColor(keyMetrics?.walletHealth?.score)} />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Wallet Health</h3>
                <p className="text-sm text-muted-foreground">Last check: {keyMetrics?.walletHealth?.lastCheck}</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${getHealthColor(keyMetrics?.walletHealth?.score)}`}>
                {keyMetrics?.walletHealth?.score}%
              </div>
              <div className="flex items-center space-x-1">
                <Icon name={getStatusIcon(keyMetrics?.walletHealth?.status)} size={14} className={getHealthColor(keyMetrics?.walletHealth?.score)} />
                <span className="text-sm capitalize text-muted-foreground">{keyMetrics?.walletHealth?.status}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            {Object.entries(keyMetrics?.walletHealth?.details)?.map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}:</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Blockchain Sync */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getHealthBgColor(keyMetrics?.blockchainSync?.score)}/10`}>
                <Icon name="Link" size={20} className={getHealthColor(keyMetrics?.blockchainSync?.score)} />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Blockchain Sync</h3>
                <p className="text-sm text-muted-foreground">Last sync: {keyMetrics?.blockchainSync?.lastSync}</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${getHealthColor(keyMetrics?.blockchainSync?.score)}`}>
                {keyMetrics?.blockchainSync?.score}%
              </div>
              <div className="flex items-center space-x-1">
                <Icon name={getStatusIcon(keyMetrics?.blockchainSync?.status)} size={14} className={getHealthColor(keyMetrics?.blockchainSync?.score)} />
                <span className="text-sm capitalize text-muted-foreground">{keyMetrics?.blockchainSync?.status}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Block Height:</span>
              <span className="text-foreground font-mono">{keyMetrics?.blockchainSync?.blockHeight?.toLocaleString()}</span>
            </div>
            {Object.entries(keyMetrics?.blockchainSync?.details)?.map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}:</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getHealthBgColor(keyMetrics?.privacySettings?.score)}/10`}>
                <Icon name="Eye" size={20} className={getHealthColor(keyMetrics?.privacySettings?.score)} />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Privacy Controls</h3>
                <p className="text-sm text-muted-foreground">{keyMetrics?.privacySettings?.activeControls} active controls</p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${getHealthColor(keyMetrics?.privacySettings?.score)}`}>
                {keyMetrics?.privacySettings?.score}%
              </div>
              <div className="flex items-center space-x-1">
                <Icon name={getStatusIcon(keyMetrics?.privacySettings?.status)} size={14} className={getHealthColor(keyMetrics?.privacySettings?.score)} />
                <span className="text-sm capitalize text-muted-foreground">{keyMetrics?.privacySettings?.status}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            {Object.entries(keyMetrics?.privacySettings?.details)?.map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}:</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Credential Activity */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon name="FileText" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">Credential Activity</h3>
                <p className="text-sm text-muted-foreground">{keyMetrics?.credentialActivity?.totalCredentials} total credentials</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                {keyMetrics?.credentialActivity?.activeCredentials}
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="CheckCircle" size={14} className="text-success" />
                <span className="text-sm text-muted-foreground">Active</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Recent Verifications:</span>
              <span className="text-foreground">{keyMetrics?.credentialActivity?.recentVerifications}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Pending Requests:</span>
              <span className="text-warning">{keyMetrics?.credentialActivity?.pendingRequests}</span>
            </div>
            {Object.entries(keyMetrics?.credentialActivity?.details)?.map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{key?.replace(/([A-Z])/g, ' $1')?.trim()}:</span>
                <span className="text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* System Status Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Activity" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">System Status: All systems operational</span>
          </div>
          <div className="text-sm text-muted-foreground">
            Last updated: {new Date()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyMetricsDisplay;